import { Injectable, inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ServiceInventoryItem, ServiceInventoryState, ServiceState } from './service-inventory.model';
import {
  deleteServiceState,
  loadServiceInventory,
  loadServiceInventoryFailure,
  loadServiceInventorySuccess,
  resetServiceInventory,
  updateServiceState,
} from './service-inventory.actions';
import { selectAllServices, selectErrorState, selectServiceByKey } from './service-inventory.selectors';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';

export type LoadInventoryResult =
  | { success: true; items: ServiceInventoryItem[] }
  | { success: false; error: string };

@Injectable({ providedIn: 'root' })
export class ServiceInventoryService {
  private _store = inject(Store<ServiceInventoryState>);
  private _actions$ = inject(Actions);

  /** Dispatch an action to load the service inventory. Returns an observable that emits once when the load succeeds or fails. */
  loadInventory(requiredKey?: string): Observable<LoadInventoryResult> {
    this._store.dispatch(loadServiceInventory({ requiredKey }));
    return this._actions$.pipe(
      ofType(loadServiceInventorySuccess, loadServiceInventoryFailure, resetServiceInventory),
      take(1),
      map((action) => {
        if (action.type === loadServiceInventorySuccess.type) {
          return { success: true as const, items: action.items };
        }
        return {
          success: false as const,
          error: action.type === loadServiceInventoryFailure.type ? action.error : 'Inventory load cancelled',
        };
      })
    );
  }

  /** Get all services as an observable */
  getInventory(): Observable<any> {
    return this._store.select(selectAllServices);
  }

  /** Get a specific service by key */
  getService(key: string): Observable<ServiceInventoryItem> {
    return this._store.select(selectServiceByKey(key));
  }

  /** Get the most recent inventory load error. */
  getError(): Observable<string | undefined> {
    return this._store.select(selectErrorState);
  }

  /** Remove inventory belonging to the previous authenticated session. */
  resetInventory(): void {
    this._store.dispatch(resetServiceInventory());
  }

  /** Update the state of a specific service */
  updateServiceState(key: string, newState: ServiceState) {
    this._store.dispatch(updateServiceState({ key, state: newState }));
  }

  /** Delete the state of a specific service */
  deleteServiceState(key: string) {
    this._store.dispatch(deleteServiceState({ key }));
  }
}
